import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { formatDate } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Banner, Button, Pill, Skeleton, useToast } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import type { FinancingTabProps } from '../record/RecordFrame';
import { CommissionCard } from '../record/Sidebar';
import {
  uploadContract,
  useChecklist,
  useFinancierRealtime,
  useOffers,
  useRefreshFinancing,
  useSignature,
  type FinancierRecordFields,
  type SignatureView,
} from './api';
import { ContractPicker, SignatureForm } from './SignatureForm';
import { AdvisorCard, CustomerProfileCard, NextStepSideCard } from './sidebars';
import styles from './financier.module.css';

/**
 * The financier's „Übersicht": before the offers the documents state (Finanzierer 05 „Unterlagen vollständig" or what is
 * missing), with offers the request and the offer list, in „Zusage" the KPIs and the signature form (Finanzierer 07),
 * afterwards the signed facts and the contract. Sidebar „Kundenprofil" + „Berater".
 */

function useTabLink(): (tab: string) => void {
  const [params, setParams] = useSearchParams();
  return (tab: string) => {
    const next = new URLSearchParams(params);
    next.set('tab', tab);
    setParams(next);
  };
}

function Kpis({ kpis }: { kpis: SignatureView['kpis'] }) {
  if (kpis.length === 0) return null;
  return (
    <div className={styles.kpis}>
      {kpis.map((kpi) => (
        <section key={kpi.label} className={clsx(styles.card, styles.kpi)} aria-label={kpi.label}>
          <div className={styles.eyebrow}>{kpi.label}</div>
          <div className={styles.kpiValue}>{kpi.value}</div>
          {kpi.sub ? <div className={styles.kpiSub}>{kpi.sub}</div> : null}
        </section>
      ))}
    </div>
  );
}

function DocumentsState({ financing }: { financing: FinancierRecordFields }) {
  const checklist = useChecklist(financing.id);
  const openTab = useTabLink();
  if (checklist.isPending) return <Skeleton variant="block" height={96} />;
  if (checklist.isError) return <Banner tone="danger">{errorMessage(checklist.error)}</Banner>;
  const data = checklist.data;
  if (data.completeCard) {
    return (
      <section className={styles.card} aria-label={data.completeCard.title}>
        <div className={styles.cardTitle}>{data.completeCard.title}</div>
        <div className={styles.completeText}>{data.completeCard.text}</div>
      </section>
    );
  }
  return (
    <section className={styles.card} aria-label="Unterlagen">
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <div className={styles.cardTitle}>{data.warning?.title ?? 'Unterlagen'}</div>
          <div className={styles.cardSubtitle}>
            {`${data.customer.done} von ${data.customer.total} vom Kunden`}
            {data.warning?.text ? ` · ${data.warning.text}` : ''}
          </div>
        </div>
        <Button variant="secondary" size="small" onClick={() => openTab('documents')}>
          Zu den Unterlagen
        </Button>
      </div>
    </section>
  );
}

function OffersState({ financing }: { financing: FinancierRecordFields }) {
  const offers = useOffers(financing.id);
  const openTab = useTabLink();
  if (offers.isPending) return <Skeleton variant="block" height={140} />;
  if (offers.isError) return <Banner tone="danger">{errorMessage(offers.error)}</Banner>;
  const view = offers.data;
  return (
    <section className={styles.card} aria-label="Angebote">
      <div className={styles.cardHead}>
        <div className={styles.cardHeadText}>
          <div className={styles.cardTitle}>Angebote</div>
          {view.request ? <div className={styles.cardSubtitle}>{view.request.text}</div> : null}
        </div>
        <Button variant="secondary" size="small" onClick={() => openTab('offers')}>
          Zu den Angeboten
        </Button>
      </div>
      <div className={styles.rows}>
        {view.rows.length === 0 ? (
          <div className={styles.offerEmpty}>Noch keine Angebote.</div>
        ) : (
          view.rows.map((row) => (
            <div key={row.id} className={clsx(styles.offerRow, row.dimmed && styles.dimmed)}>
              <span style={{ minWidth: 0 }}>
                <span className={styles.offerBank}>{row.bank}</span>
                {row.sourceLine ? <span className={styles.offerSource}>{row.sourceLine}</span> : null}
              </span>
              <span style={{ display: 'flex' }}>
                <Pill variant={row.tone}>{row.statusLabel}</Pill>
              </span>
              <span className={styles.offerLine}>{row.line}</span>
              <span className={styles.offerValid}>{row.validLine}</span>
              <span />
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function SignedState({
  financing,
  signed,
  canUpload,
}: {
  financing: FinancierRecordFields;
  signed: NonNullable<SignatureView['signed']>;
  canUpload: boolean;
}) {
  const refresh = useRefreshFinancing(financing.id);
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [pending, setPending] = useState(false);

  async function upload() {
    if (!file) return;
    setPending(true);
    try {
      await uploadContract(financing.id, file);
      setFile(null);
      toast({ message: 'Vertrag hochgeladen.' });
      await refresh();
    } catch (error) {
      toast({ message: errorMessage(error) });
    } finally {
      setPending(false);
    }
  }

  const rows: Array<[string, string]> = [
    ['Unterschrieben am', formatDate(`${signed.on}T12:00:00Z`)],
    ['Bank', signed.bank],
    ['Wie wurde unterschrieben?', signed.methodLabel],
    ['Vertrag', signed.contract ? signed.contract.name : 'fehlt noch'],
  ];
  return (
    <section className={clsx(styles.card, styles.cardLarge)} aria-label="Finanzierungsvertrag unterschrieben">
      <div className={styles.cardTitle}>Finanzierungsvertrag unterschrieben</div>
      <dl className={clsx(styles.kvList, styles.signedRows)}>
        {rows.map(([label, value]) => (
          <div key={label} className={styles.kv}>
            <dt className={styles.kvLabel}>{label}</dt>
            <dd className={styles.kvValue}>{value}</dd>
          </div>
        ))}
      </dl>
      {!signed.contract && canUpload ? (
        <div className={styles.contractField} style={{ marginTop: 14 }}>
          <span className={styles.formLabel}>Vertrag nachreichen</span>
          <ContractPicker file={file} onFile={setFile} disabled={pending} />
          <div className={styles.cardFooterPlain} style={{ marginTop: 6 }}>
            <span className={styles.footNote}>
              Mit dem Vertrag ist die Task „Vertrag nachreichen“ erledigt.
            </span>
            <span className={styles.footActions}>
              <Button loading={pending} disabled={!file} onClick={() => void upload()}>
                Vertrag hochladen
              </Button>
            </span>
          </div>
        </div>
      ) : null}
    </section>
  );
}

function CommitmentState({
  financing,
  onChanged,
}: {
  financing: FinancierRecordFields;
  onChanged: () => void;
}) {
  const signature = useSignature(financing.id);
  const refresh = useRefreshFinancing(financing.id);
  if (signature.isPending) {
    return (
      <>
        <div className={styles.kpis}>
          <Skeleton variant="block" height={96} />
          <Skeleton variant="block" height={96} />
          <Skeleton variant="block" height={96} />
        </div>
        <Skeleton variant="block" height={280} />
      </>
    );
  }
  if (signature.isError) return <Banner tone="danger">{errorMessage(signature.error)}</Banner>;
  const view = signature.data;
  const allowed = financing.actions.recordSignature;
  return (
    <>
      <Kpis kpis={view.kpis} />
      {view.signed ? (
        <SignedState financing={financing} signed={view.signed} canUpload={financing.status === 'active'} />
      ) : allowed.allowed ? (
        <SignatureForm
          financing={financing}
          variant="card"
          onDone={() => {
            void refresh();
            onChanged();
          }}
        />
      ) : allowed.reason ? (
        <Banner tone="neutral">{allowed.reason}</Banner>
      ) : null}
    </>
  );
}

export function FinancierOverview(props: FinancingTabProps) {
  const record: FinancierRecordFields = props.financing;
  useFinancierRealtime(record.id);
  const stage = record.stage.key;
  const documentsStage = stage === 'documents';
  const signatureStage = stage === 'commitment' || stage === 'signed' || stage === 'disbursed';
  const checklist = useChecklist(record.id, documentsStage);
  const completeBeforeRequest = documentsStage && checklist.data?.complete === true;

  return (
    <div className={clsx(styles.content, styles.contentTall)}>
      <div className={styles.main}>
        {documentsStage ? <DocumentsState financing={record} /> : null}
        {stage === 'requested' || stage === 'offers' || stage === 'lost' ? (
          <OffersState financing={record} />
        ) : null}
        {signatureStage ? <CommitmentState financing={record} onChanged={() => props.onChanged()} /> : null}
      </div>
      <aside className={styles.side}>
        {completeBeforeRequest ? <NextStepSideCard /> : null}
        <CustomerProfileCard financing={record} />
        <AdvisorCard financing={record} />
        <CommissionCard financing={props.financing} onChanged={() => props.onChanged()} />
      </aside>
    </div>
  );
}
